// Login.jsx - Version corrigée avec affichage côte à côte sur web
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import { APP_CONFIG } from '../config/appConfig';
import '../styles/lmsiss.css';

export default function Login() {
  const navigate = useNavigate();
  const { login, verifyMfa, resendOtp } = useAuth();
  
  const [step, setStep] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Login form
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // MFA form
  const [otpCode, setOtpCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [mfaType, setMfaType] = useState('');
  const [remainingAttempts, setRemainingAttempts] = useState(3);
  const [otpTimer, setOtpTimer] = useState(120);
  const [canResend, setCanResend] = useState(false);
  
  const timerRef = useRef(null);

  // Timer pour le compte à rebours OTP
  useEffect(() => {
    if (step === 'mfa') {
      timerRef.current = setInterval(() => {
        setOtpTimer(prev => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [step]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const result = await login(username, password);
    
    if (result.success) {
      setSuccess('Connexion réussie !');
      const redirectPath = result.redirectPath || '/dashboard';
      setTimeout(() => {
        navigate(redirectPath);
      }, 500);
    } else if (result.mfaRequired) {
      setTempToken(result.tempToken);
      setMfaType(result.mfaType);
      setRemainingAttempts(3);
      setOtpTimer(120);
      setCanResend(false);
      setStep('mfa');
      setSuccess(`Code OTP envoyé par ${result.mfaType === 'EMAIL' ? 'email' : 'SMS'}`);
      setLoading(false);
    } else {
      setError(result.error || 'Identifiants incorrects');
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const result = await verifyMfa(tempToken, otpCode);
    
    if (result.success) {
      setSuccess('Authentification réussie !');
      const redirectPath = result.redirectPath || '/dashboard';
      setTimeout(() => {
        navigate(redirectPath);
      }, 500);
    } else {
      if (result.remainingAttempts !== undefined) {
        setRemainingAttempts(result.remainingAttempts);
        setError(`Code OTP invalide - ${result.remainingAttempts} tentative(s) restante(s)`);
      } else {
        setError(result.error || 'Code OTP invalide');
      }
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    const result = await resendOtp(tempToken);
    
    if (result.success) {
      setOtpTimer(120);
      setCanResend(false);
      setRemainingAttempts(3);
      setSuccess('Nouveau code OTP envoyé !');
      
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setOtpTimer(prev => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      setError(result.error || 'Erreur lors du renvoi');
    }
    setLoading(false);
  };

  const handleBackToLogin = () => {
    setStep('login');
    setError('');
    setSuccess('');
    setOtpCode('');
    clearInterval(timerRef.current);
  };

  return (
    <div className="min-h-screen bg-[#f0f2f5] flex items-center justify-center p-3 sm:p-4">
      <div className="w-full max-w-6xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row">
        
        {/* ============================================ */}
        {/* COLONNE GAUCHE : Bannière ISS (Vert) - 40% */}
        {/* ============================================ */}
        <div 
          className="bg-green-800 p-5 sm:p-6 md:p-12 flex flex-col items-center justify-center text-white relative overflow-hidden w-full md:w-2/5 min-h-[300px] md:min-h-[600px]"
        >
          {/* Éléments décoratifs */}
          <div className="absolute top-0 right-0 w-32 h-32 md:w-64 md:h-64 bg-white/5 rounded-full -translate-y-1/3 translate-x-1/3"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 md:w-48 md:h-48 bg-white/5 rounded-full translate-y-1/3 -translate-x-1/3"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 md:w-80 md:h-80 bg-white/5 rounded-full"></div>
          
          <div className="relative z-10 text-center w-full flex flex-col items-center">
            {/* Logo sur fond blanc - centré */}
            <div className="w-20 h-20 sm:w-28 sm:h-28 md:w-48 md:h-48 mx-auto bg-white rounded-3xl p-2 sm:p-3 md:p-5 flex items-center justify-center mb-3 sm:mb-4 md:mb-8 shadow-2xl shadow-black/20">
              <img 
                src="/images/logoiss.png" 
                alt="ISS Logo" 
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const parent = e.target.parentNode;
                  parent.innerHTML = `
                    <div class="text-[#009640] text-6xl font-serif font-black">ISS</div>
                    <div class="text-[#009640]/60 text-[10px] mt-1 uppercase tracking-widest">Ivoire Soft Services</div>
                  `;
                }}
              />
            </div>

            <div className="items-center justify-center p-2 md:p-5">
              {/* Titre - Texte BLANC en Georgia - centré */}
              <h3 className="text-2xl md:text-3xl font-serif font-bold tracking-tight leading-tight text-white text-center">
                {APP_CONFIG.LOGIN_PAGE.TITRE}
              </h3>
            </div>
            
            {/* Trait orange - centré */}
            <div className="w-16 h-0.5 bg-[#ef7c00] mx-auto my-4 rounded-full"></div>
            
            <div className="items-center justify-center mb-4">
              {/* Sous-titre - Texte BLANC en Georgia - centré */}
              <p className="text-white text-xs uppercase tracking-[0.25em] font-medium font-serif text-center">
                {APP_CONFIG.LOGIN_PAGE.SOUS_TITRE}
              </p>
            </div>
            
            {/* Séparateur avec point orange - centré */}
            <div className="flex items-center justify-center gap-4 mt-4 md:mt-8">
              <div className="w-8 h-px bg-white/20"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-[#ef7c00]"></div>
              <div className="w-8 h-px bg-white/20"></div>
            </div>

            {/* Badge - Texte BLANC en Georgia - centré */}
            <div className="mt-4 md:mt-8 inline-block px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/10">
              <span className="text-[10px] uppercase tracking-[0.15em] text-white font-medium font-serif">
                Plateforme de Formation et d'Evaluation
              </span>
            </div>

            {/* Copyright - Texte BLANC en Georgia - centré */}
            <p className="text-white/40 text-[10px] mt-4 md:mt-10 font-light tracking-wider font-serif text-center">
              © 2026 Ivoire Soft Services
            </p>
          </div>
        </div>

        {/* ============================================ */}
        {/* COLONNE DROITE : Formulaire - 60% */}
        {/* ============================================ */}
        <div className="p-6 sm:p-8 md:p-12 lg:p-14 flex flex-col justify-center bg-white min-h-[400px] w-full md:w-3/5">
          <div className="max-w-md mx-auto w-full">
            {/* En-tête du formulaire - Georgia */}
            <div className="mb-6 md:mb-10">
              <h2 className="text-2xl sm:text-3xl md:text-4xl text-center font-serif font-bold text-[#1a1a2e] tracking-tight">
                {step === 'login' ? (
                  <>
                    CONNEXION <span className="text-[#009640]">👋</span>
                  </>
                ) : (
                  'Vérification 🔐'
                )}
              </h2>
              <p className="text-base text-center text-[#6b7280] mt-2 leading-relaxed font-serif">
                {step === 'login' 
                  ? APP_CONFIG.LOGIN_PAGE.DESCRIPTION
                  : `Un code à 6 chiffres a été envoyé par ${mfaType === 'EMAIL' ? 'email' : 'SMS'}`}
              </p>
            </div>

            {/* Messages d'erreur / succès */}
            {error && (
              <div className="mb-5 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-xl text-red-700 text-base flex items-start gap-3 font-serif">
                <span className="text-red-500 text-xl">✕</span>
                <span>{error}</span>
              </div>
            )}
            {success && (
              <div className="mb-5 p-4 bg-emerald-50 border-l-4 border-emerald-500 rounded-r-xl text-emerald-700 text-base flex items-start gap-3 font-serif">
                <span className="text-emerald-500 text-xl">✓</span>
                <span>{success}</span>
              </div>
            )}

            {step === 'login' ? (
              // ==========================================
              // FORMULAIRE DE CONNEXION
              // ==========================================
              <form onSubmit={handleLogin} className="space-y-6">
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-[#374151] uppercase tracking-wider mb-2 font-serif">
                    Identifiant
                  </label>
                  <div className="flex items-center gap-3">
                    <span className="text-[#9ca3af]">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </span>
                    <input 
                      type="text" 
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="flex-1 bg-[#f8fafc] border-2 border-[#e5e7eb] rounded-xl py-4 text-base text-lg outline-none focus:border-[#009640] focus:ring-4 focus:ring-[#009640]/10 transition-all duration-200 font-serif"
                      style={{ paddingLeft: '16px' }}
                      placeholder="Email ou numéro de téléphone"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>
                
                <div className="mb-6">
                  <div className="flex justify-between items-center">
                    <label className="block text-sm font-semibold text-[#374151] uppercase tracking-wider font-serif">
                      Mot de passe
                    </label>
                    <Link 
                      to="/forgot-password" 
                      className="text-sm text-[#009640] hover:text-[#007a33] hover:underline transition font-medium font-serif"
                    >
                      Mot de passe oublié ?
                    </Link>
                  </div>
                  <div className="relative flex items-center gap-3">
                    <span className="text-[#9ca3af]">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </span>
                    <input 
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-[#f8fafc] border-2 border-[#e5e7eb] rounded-xl pl-12 pr-12 py-4 text-base text-lg outline-none focus:border-[#009640] focus:ring-4 focus:ring-[#009640]/10 transition-all duration-200 font-serif"
                      style={{ paddingLeft: '16px' }}
                      placeholder="••••••••"
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9ca3af] hover:text-[#374151] transition"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                
                <div className="mb-6">
                  <button 
                    type="submit" 
                    className="w-full bg-[#009640] hover:bg-[#007a33] text-white font-semibold py-4 rounded-xl transition-all duration-200 shadow-lg shadow-[#009640]/25 hover:shadow-[#009640]/40 hover:-translate-y-0.5 active:translate-y-0 text-base font-serif"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-3">
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Connexion en cours...
                      </span>
                    ) : 'Se connecter'}
                  </button>
                </div>
                
                <p className="text-center text-base text-[#6b7280] font-serif pt-2">
                  Pas encore de compte ?{' '}
                  <Link to="/register" className="text-[#009640] font-semibold hover:text-[#007a33] hover:underline transition">
                    Créer un compte
                  </Link>
                </p>
              </form>
            ) : (
              // ==========================================
              // FORMULAIRE MFA
              // ==========================================
              <form onSubmit={handleMfaVerify} className="space-y-6">
                <div className="text-center py-3">
                  <div className="text-5xl mb-3">🔐</div>
                  <p className="text-lg text-[#374151] font-medium font-serif">
                    Saisissez le code de vérification
                  </p>
                  <p className="text-sm text-[#6b7280] mt-1.5 flex items-center justify-center gap-1.5 font-serif">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#009640]"></span>
                    {mfaType === 'EMAIL' ? '📧 Envoyé par email' : '📱 Envoyé par SMS'}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-[#374151] uppercase tracking-wider mb-2 text-center font-serif">
                    Code OTP
                  </label>
                  <input 
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full bg-[#f8fafc] border-2 border-[#e5e7eb] rounded-xl px-2 sm:px-4 py-4 sm:py-5 text-center text-xl sm:text-2xl md:text-3xl font-mono font-bold tracking-[0.25em] sm:tracking-[0.4em] md:tracking-[0.5em] outline-none focus:border-[#009640] focus:ring-4 focus:ring-[#009640]/10 transition-all duration-200"
                    placeholder="• • • • • •"
                    maxLength={6}
                    required
                    disabled={loading}
                    autoFocus
                  />
                </div>

                {remainingAttempts < 3 && (
                  <p className="text-sm text-red-500 text-center font-medium flex items-center justify-center gap-1.5 font-serif">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                    Tentatives restantes : {remainingAttempts}
                  </p>
                )}

                <div className="flex justify-between items-center text-sm bg-[#f8fafc] rounded-xl px-5 py-3.5 border border-[#e5e7eb] font-serif">
                  <span className="flex items-center gap-2 text-[#6b7280]">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-mono font-medium">
                      {otpTimer > 0 ? formatTime(otpTimer) : 'Code expiré'}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={!canResend || loading}
                    className={`font-medium transition font-serif ${
                      canResend && !loading 
                        ? 'text-[#009640] hover:text-[#007a33]' 
                        : 'text-[#d1d5db] cursor-not-allowed'
                    }`}
                  >
                    Renvoyer le code
                  </button>
                </div>

                <button 
                  type="submit" 
                  className="w-full bg-[#ef7c00] hover:bg-[#cc6a00] text-white font-semibold py-4 rounded-xl transition-all duration-200 shadow-lg shadow-[#ef7c00]/25 hover:shadow-[#ef7c00]/40 hover:-translate-y-0.5 active:translate-y-0 text-base font-serif"
                  disabled={loading || otpCode.length < 6}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-3">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Vérification...
                    </span>
                  ) : 'Valider le code'}
                </button>

                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="w-full text-sm text-[#6b7280] hover:text-[#374151] transition font-medium flex items-center justify-center gap-2 font-serif"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Retour à la connexion
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}